import axios from 'axios';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Button, Grid } from '@mui/material';
import { SnackbarKey, useSnackbar } from 'notistack';
import { UserDataTable } from '../DataTable/UserDataTable';
import { IAdminStatistics, IAdminUserStatistics } from './interfaces';
import { formatDate } from '../../utils/commonFunctions';
import { PLATFORM_TAG, SUBSCRIPTION_TAG } from '../../constants/constants';
import { DatePickerStatistics } from './DatePickerStatistics';
import { getReport } from './reportsApi';
import { getAuthToken } from '../../../utils/authHelpers';
import { ITokens } from '../../interfaces/auth';
import { ICreatorTabStatistics, IDateStatistics } from './interfaces';
import {
  optionsError,
  optionsLoading,
  optionsSuccess,
} from './constants';
import { LoadingOverlay } from '../LoadingOverlay';

const validationSchema = Yup.object().shape({
  registeredRange: Yup.object().shape({
    startDate : Yup.date().nullable(),
    endDate   : Yup.date().nullable(),
  }),
  activityRange: Yup.object().shape({
    startDate : Yup.date().nullable(),
    endDate   : Yup.date().nullable(),
  }),
  softDeleteRange: Yup.object().shape({
    startDate : Yup.date().nullable(),
    endDate   : Yup.date().nullable(),
  }),
  hardDeleteRange: Yup.object().shape({
    startDate : Yup.date().nullable(),
    endDate   : Yup.date().nullable(),
  }),
  isDeleted: Yup.boolean(),
});

const DateWrapper = styled(Grid)`
  margin: 25px 0 50px;
`;

const PageContainer = styled.div`
  background-color: rgb(247, 250, 253);
  padding: 2px 10px 0 40px;
`;

const SelectDateBlock = styled(Grid)`
  display: flex;

  input {
    width: auto;
  }

  h3 {
    font-size: 22px;
    font-weight: 505;
    color: rgb(70, 108, 157);
    align-items: center;
    display: flex;
    margin: 0 25px 0 -8px;

    ${ ({ theme: { breakpoints } }) => breakpoints.down('sm') } {
      font-size: 15px;
    }
  }
`;

const FormButton = styled(Button)`&& {
  border-radius: 9px;
  background-color: #547cd3;
  float: right;
  width: 80%;
  
  &:hover {
    background-color: #4b6ebb;
  }
}`;

export const CreatorTabStatistics: FC<ICreatorTabStatistics> = ({ familyCreator }) => {
  const defaultDate: IDateStatistics = {
    registeredRange: {
      startDate : null,
      endDate   : null,
    },
    activityRange: {
      startDate : null,
      endDate   : null,
    },
    softDeleteRange: {
      startDate : null,
      endDate   : null,
    },
    hardDeleteRange: {
      startDate : null,
      endDate   : null,
    },
    isDeleted           : false,
    searchTable         : null,
    platformType        : null,
    paymentPlatformType : null,
    familyRole          : null,
  };

  const authToken: ITokens = getAuthToken();

  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/;

  const testLink = 'http://localhost:3000';
  const qaLink = 'https://familycore-qa-app-service-ui';

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [snackbarKey, setSnackbarKey] = useState<SnackbarKey | undefined>(undefined);

  const [selectedDate, setSelectedDate] = useState<IDateStatistics>(defaultDate);
  const [isProdVersion, setIsProdVersion] = useState<boolean>(false);
  const [statisticsDataLength, setstatisticsDataLength] = useState<number>(0);
  const [statisticsData, setStatisticsData] = useState<IAdminStatistics[] | IAdminUserStatistics[] | undefined | null | any>();

  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError]     = useState<boolean>(false);

  const [sortOrderBy, setSortOrderBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>();

  const formikRef = useRef<FormikProps<any> | null>(null);

  const currentUrl = window.location.href;

  const hasMountedOnce = useRef(false);

  useEffect(() => {
    if (currentUrl.startsWith(testLink) || currentUrl.startsWith(qaLink)) {
      setIsProdVersion(false);
    } else {
      setIsProdVersion(true);
    }
  }, [currentUrl]);

  function checkDateFormat(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;

    if (isoDatePattern.test(dateStr)) {
      return dateStr.split('.')[0];
    }

    try {
      const date = new Date(dateStr);
      return date.toISOString().split('.')[0];
    } catch {
      return null;
    }
  }

  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = -timezoneOffsetMinutes / 60;
  console.log(`UTC offset: ${offsetHours} hours`);

  const formatParam = (value: string | null | undefined, paramName: string) => {
    const formatted = checkDateFormat(value);
    return formatted ? `${paramName}=${formatted}Z` : '';
  };

  function toEndOfDay(date: Date | string | null | undefined, offsetHours: number = 0): string | null {
    if (!date) return null;

    try {
      const d = new Date(date);

      d.setHours(23, 59, 59, 999);

      d.setHours(d.getHours() + offsetHours);

      return d.toISOString().split('.')[0] + 'Z';
    } catch {
      return null;
    }
  }

  const getStatisticsCreatorsInfo = (
    date: IDateStatistics,
    setData: any,
    skip = 0,
    take = 10,
    orderBy?: string,
    order?: 'asc' | 'desc'
  ) => {
    setIsTableLoading(true);

    const queryParams = [
      `IsFamilyCreator=${true}`,
      `IsDeleted=${date.isDeleted}`,
      formatParam(date.registeredRange.startDate, 'RegisteredFrom'),
      formatParam(
        date.registeredRange.endDate || toEndOfDay(date.registeredRange.startDate, offsetHours),
        'RegisteredTo'
      ),
      formatParam(date.softDeleteRange.startDate, 'DeletedFrom'),
      formatParam(
        date.softDeleteRange.endDate || toEndOfDay(date.softDeleteRange.startDate, offsetHours),
        'DeletedTo'
      ),
      formatParam(date.hardDeleteRange.startDate, 'HardDeletedFrom'),
      formatParam(
        date.hardDeleteRange.endDate || toEndOfDay(date.hardDeleteRange.startDate, offsetHours),
        'HardDeletedTo'
      ),
      formatParam(date.activityRange.startDate, 'LastVisitFrom'),
      formatParam(
        date.activityRange.endDate || toEndOfDay(date.activityRange.startDate, offsetHours),
        'LastVisitTo'
      ),
      date.paymentPlatformType ? `PaymentPlatformType=${date.paymentPlatformType}` : '',
      date.familyRole ? `FamilyRole=${date.familyRole}` : '',
      date.platformType ? `PlatformType=${date.platformType}` : '',
      date.searchTable ? `Search=${date.searchTable}` : '',
      `Skip=${skip}`,
      `Take=${take}`,
      orderBy ? `OrderBy=${orderBy}` : '',
      order ? `Order=${order === 'asc' ? 'Ascending' : 'Descending'}` : '',
    ].filter(Boolean).join('&');

    const queryString = `/api/v1/Statistics/users?${queryParams}`;

    axios.get(queryString)
      .then((data) => {
        setStatisticsData(data?.data?.data);
        setstatisticsDataLength(data?.data?.totalCount);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsTableLoading(false);
      });
  };

  const getStatisticsUsersInfo = (
    date: IDateStatistics,
    setData: any,
    skip = 0,
    take = 10,
    orderBy?: string,
    order?: 'asc' | 'desc'
  ) => {
    setIsTableLoading(true);

    const queryParams = [
      `IsFamilyCreator=${false}&`,
      `IsDeleted=${date.isDeleted}`,
      formatParam(date.registeredRange.startDate, '&RegisteredFrom'),
      formatParam(
        date.registeredRange.endDate || toEndOfDay(date.registeredRange.startDate, offsetHours),
        'RegisteredTo'
      ),
      formatParam(date.softDeleteRange.startDate, 'DeletedFrom'),
      formatParam(
        date.softDeleteRange.endDate || toEndOfDay(date.softDeleteRange.startDate, offsetHours),
        'DeletedTo'
      ),
      formatParam(date.hardDeleteRange.startDate, 'HardDeletedFrom'),
      formatParam(
        date.hardDeleteRange.endDate || toEndOfDay(date.hardDeleteRange.startDate, offsetHours),
        'HardDeletedTo'
      ),
      formatParam(date.activityRange.startDate, 'LastVisitFrom'),
      formatParam(
        date.activityRange.endDate || toEndOfDay(date.activityRange.startDate, offsetHours),
        'LastVisitTo'
      ),
      date.paymentPlatformType ? `PaymentPlatformType=${date.paymentPlatformType}` : '',
      date.familyRole ? `FamilyRole=${date.familyRole}` : '',
      date.platformType ? `PlatformType=${date.platformType}` : '',
      date.searchTable ? `Search=${date.searchTable}` : '',
      `Skip=${skip}`,
      `Take=${take}`,
      orderBy ? `OrderBy=${orderBy}` : '',
      order ? `Order=${order === 'asc' ? 'Ascending' : 'Descending'}` : '',
    ].filter(Boolean).join('&');

    const queryString = `/api/v1/Statistics/users?${queryParams}`;

    axios.get(queryString)
      .then((data) => {
        setStatisticsData(data?.data?.data);
        setstatisticsDataLength(data?.data?.totalCount);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsTableLoading(false);
      });
  };

  const getUnSubscripeGoogleEvent = () => {
    axios.get('/api/v1/statistics/full-report')
      .then((res) => {
        enqueueSnackbar('Full report loaded', optionsSuccess);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar('Failed to load full report', optionsError);
      });
  };

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setIsSuccess(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isLoading && !snackbarKey) {
      const key = enqueueSnackbar(`Please wait. Don't refresh and don't close page.`, optionsLoading);
      setSnackbarKey(key);
    } else if (isSuccess && snackbarKey) {
      closeSnackbar(snackbarKey);
      setSnackbarKey('');
      enqueueSnackbar('Report downloaded', optionsSuccess);
    } else if (isError) {
      closeSnackbar(snackbarKey);
      setSnackbarKey(undefined);
      enqueueSnackbar('Error', optionsError);
    }
  }, [isLoading, isSuccess, isError, snackbarKey, enqueueSnackbar, closeSnackbar]);

  const getFamilyRole = (role: string | null): string =>
    role?.split(/(?=[A-Z])/).join(' ') || '-';

  const getUserTableRow = (itemData: IAdminUserStatistics[] | undefined) => {
    if (!itemData) return [];
    return itemData.map((item) => ({
      firstName           : item?.firstName || '-',
      lastName            : item?.lastName || '-',
      middleName          : item?.middleName || '-',
      familyName          : item?.familyName || '-',
      familyRole          : getFamilyRole(item?.familyRole),
      country             : item?.country || '-',
      registeredAt        : formatDate(item?.registeredAt) || '-',
      lastVisitAt         : formatDate(item?.lastVisitAt) || '-',
      deletedAt           : formatDate(item?.deletedAt) || '-',
      hardDelete          : formatDate(item?.hardDelete) || '-',
      email               : item?.email || '-',
      phoneNumber         : item?.phoneNumber || '-',
      platformType        : PLATFORM_TAG[item?.platformType as keyof typeof PLATFORM_TAG] || '-',
      paymentPlatformType : SUBSCRIPTION_TAG[item?.paymentPlatformType as keyof typeof SUBSCRIPTION_TAG] || '-',
    }));
  };

  const getTableRow = (itemData: IAdminUserStatistics[] | undefined) => {
    if (!itemData) return [];

    return itemData.map((item) => ({
      firstName           : item?.firstName || '-',
      lastName            : item?.lastName || '-',
      middleName          : item?.middleName || '-',
      familyName          : item?.familyName || '-',
      familyRole          : getFamilyRole(item?.familyRole),
      country             : item?.country || '-',
      registeredAt        : formatDate(item?.registeredAt) || '-',
      lastVisitAt         : formatDate(item?.lastVisitAt) || '-',
      deletedAt           : formatDate(item?.deletedAt) || '-',
      hardDelete          : formatDate(item?.hardDelete) || '-',
      email               : item?.email || '-',
      phoneNumber         : item?.phoneNumber || '-',
      platformType        : PLATFORM_TAG[item?.platformType as keyof typeof PLATFORM_TAG] || '-',
      paymentPlatformType : SUBSCRIPTION_TAG[item?.paymentPlatformType as keyof typeof SUBSCRIPTION_TAG] || '-',
    }));
  };

  const getCorrectTable = (data: any) => {
    if (!data) return [];
    return familyCreator ? getTableRow(data) : getUserTableRow(data);
  };

  const isDateFilterApplied = (date: IDateStatistics): boolean => {
    const isRangeFilled = (range: { startDate: any; endDate: any }) =>
      !!range.startDate || !!range.endDate;

    return (
      isRangeFilled(date.registeredRange) ||
      isRangeFilled(date.activityRange) ||
      isRangeFilled(date.softDeleteRange) ||
      isRangeFilled(date.hardDeleteRange) ||
      date.isDeleted !== defaultDate.isDeleted
    );
  };

  const isDefaultFilters = (d: IDateStatistics) => {
    const empty = (r: { startDate: any; endDate: any }) => !r.startDate && !r.endDate;

    return (
      empty(d.registeredRange) &&
      empty(d.activityRange) &&
      empty(d.softDeleteRange) &&
      empty(d.hardDeleteRange) &&
      d.isDeleted === false &&
      !d.searchTable &&
      !d.platformType &&
      !d.paymentPlatformType &&
      !d.familyRole
    );
  };

  useEffect(() => {
    const values = formikRef.current?.values ?? defaultDate;

    if (isDefaultFilters(values)) return;

    const skip = 0;
    const take = pageSize;
    setPage(0);

    if (familyCreator) {
      getStatisticsCreatorsInfo(values, setStatisticsData, skip, take, sortOrderBy, sortOrder);
    } else {
      getStatisticsUsersInfo(values, setStatisticsData, skip, take, sortOrderBy, sortOrder);
    }
  }, [familyCreator, pageSize, sortOrderBy, sortOrder]);

  const getUserReports = (date: IDateStatistics) => {
    const queryParams = [
      `IsDeleted=${date.isDeleted}`,
      `IsFamilyCreator=false`,
      formatParam(date.registeredRange.startDate, 'RegisteredFrom'),
      formatParam(date.registeredRange.endDate, 'RegisteredTo'),
      formatParam(date.softDeleteRange.startDate, 'DeletedFrom'),
      formatParam(date.softDeleteRange.endDate, 'DeletedTo'),
      formatParam(date.hardDeleteRange.startDate, 'HardDeletedFrom'),
      formatParam(date.hardDeleteRange.endDate, 'HardDeletedTo'),
      formatParam(date.activityRange.startDate, 'LastVisitFrom'),
      formatParam(date.activityRange.endDate, 'LastVisitTo'),
      date.paymentPlatformType ? `PaymentPlatformType=${date.paymentPlatformType}` : '',
      date.familyRole ? `FamilyRole=${date.familyRole}` : '',
      date.platformType ? `PlatformType=${date.platformType}` : '',
      date.searchTable ? `Search=${date.searchTable}` : '',
    ].filter(Boolean).join('&');

    getReport({
      requestString : queryParams,
      typeReport    : 'users-excel-report',
      fileName      : 'Statistics-Users.xlsx',
      authToken     : authToken.accessToken,
      isProdVersion,
      setIsLoading,
      setIsSuccess,
      setIsError,
    });
  };

  const getSuperAdminReports = (date: IDateStatistics) => {
    const queryParams = [
      `IsDeleted=${date.isDeleted}`,
      `IsFamilyCreator=true`,
      formatParam(date.registeredRange.startDate, 'RegisteredFrom'),
      formatParam(date.registeredRange.endDate, 'RegisteredTo'),
      formatParam(date.softDeleteRange.startDate, 'DeletedFrom'),
      formatParam(date.softDeleteRange.endDate, 'DeletedTo'),
      formatParam(date.hardDeleteRange.startDate, 'HardDeletedFrom'),
      formatParam(date.hardDeleteRange.endDate, 'HardDeletedTo'),
      formatParam(date.activityRange.startDate, 'LastVisitFrom'),
      formatParam(date.activityRange.endDate, 'LastVisitTo'),
      date.paymentPlatformType ? `PaymentPlatformType=${date.paymentPlatformType}` : '',
      date.familyRole ? `FamilyRole=${date.familyRole}` : '',
      date.platformType ? `PlatformType=${date.platformType}` : '',
    ].filter(Boolean).join('&');

    getReport({
      requestString : queryParams,
      typeReport    : 'users-excel-report',
      fileName      : 'Statistics-Users.xlsx',
      authToken     : authToken.accessToken,
      isProdVersion,
      setIsLoading,
      setIsSuccess,
      setIsError,
    });
  };

  useEffect(() => {
    if (formikRef.current) {
      const formikValues = formikRef.current.values;
      setSelectedDate(formikValues);
    }
  }, [formikRef.current?.values]);

  return (
    <PageContainer>
      <div>
        <DateWrapper container xs={12} sm={12} md={12}>
          <SelectDateBlock item xs={12} sm={12} md={12}>
            <Formik
              innerRef={formikRef}
              initialValues={{
                registeredRange: {
                  startDate : null,
                  endDate   : null,
                },
                activityRange: {
                  startDate : null,
                  endDate   : null,
                },
                softDeleteRange: {
                  startDate : null,
                  endDate   : null,
                },
                hardDeleteRange: {
                  startDate : null,
                  endDate   : null,
                },
                isDeleted: false,
              }}
              validationSchema={validationSchema}
              onSubmit={() => {
                console.log(' ');
              }}
            >
              {({ values, setFieldValue }) => (
                <DatePickerStatistics
                  formikRef={formikRef}
                  values={values}
                  setFieldValue={setFieldValue}
                  getUserReports={getUserReports}
                  getSuperAdminReports={getSuperAdminReports}
                  setData={setStatisticsData}
                  familyCreator={familyCreator}
                  getStatisticsCreatorsInfo={getStatisticsCreatorsInfo}
                  getStatisticsUsersInfo={getStatisticsUsersInfo}
                  getUnSubscripeGoogleEvent={getUnSubscripeGoogleEvent}
                  setPage={setPage}
                  pageSize={pageSize}
                />
              )}

            </Formik>
          </SelectDateBlock>
        </DateWrapper>

        <Grid container xs={12} sm={12} md={12} spacing={2}>
          <UserDataTable
            familyCreator={familyCreator}
            statisticsData={(statisticsData && getCorrectTable(statisticsData)) || []}
            totalCount={statisticsDataLength}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={(newPage, newPageSize) => {
              const skip = newPage * newPageSize;
              const take = newPageSize;

              setPage(newPage);
              setPageSize(newPageSize);

              if (familyCreator) {
                getStatisticsCreatorsInfo(selectedDate, setStatisticsData, skip, take, sortOrderBy, sortOrder);
              } else {
                getStatisticsUsersInfo(selectedDate, setStatisticsData, skip, take, sortOrderBy, sortOrder);
              }
            }}
            onSortChange={(orderByValue, orderValue) => {
              setSortOrderBy(orderByValue);
              setSortOrder(orderValue);

              const skip = page * pageSize;
              const take = pageSize;

              if (familyCreator) {
                getStatisticsCreatorsInfo(selectedDate, setStatisticsData, skip, take, orderByValue, orderValue);
              } else {
                getStatisticsUsersInfo(selectedDate, setStatisticsData, skip, take, orderByValue, orderValue);
              }
            }}
          />
        </Grid>
      </div>
      <LoadingOverlay loading={isTableLoading} />
    </PageContainer>
  );
};
